import React, { useMemo, useState, Fragment, createElement } from 'react';
import { Crown, Check, CheckCircle2, ShieldCheck, LayoutGrid, Target, Megaphone, Zap, TrendingUp, ShoppingBag, X, ArrowRight, Clock, Eye, MousePointerClick, DollarSign, ChevronDown, ChevronUp, RotateCcw, CalendarPlus, Rocket, ArrowUpCircle, Mail, Sparkles, BarChart3, Wallet, AlertCircle, Globe, BookOpen, Pin, FileText, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { ALL_AD_SLOTS } from '../ads/adData';
interface PromotionTabProps {
  showToast: (msg: string) => void;
  setActiveAutoService: (service: string | null) => void;
  setActiveTab?: (tab: string) => void;
}
interface AdOrder {
  id: string;
  type: 'banner' | 'email' | 'pin';
  name: string;
  status: 'active' | 'completed' | 'pending_moderation' | 'pending_payment' | 'approved' | 'rejected' | 'pending';
  startDate: string;
  endDate: string | null;
  price: number;
  views: number;
  clicks: number;
  orders: number;
  income: number;
  rejectionReason?: string;
  moderatedAt?: string;
  // Creative materials
  adTitle?: string;
  adDescription?: string;
  adLink?: string;
  adImageUrl?: string;
}
// Map ALL_AD_SLOTS to astrologer-friendly format
const SLOT_ICON_MAP: Record<string, {
  icon: any;
  iconCls: string;
  activeCls: string;
}> = {
  Banner: {
    icon: LayoutGrid,
    iconCls: 'bg-indigo-100 text-indigo-600',
    activeCls: 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500'
  },
  Native: {
    icon: Layers,
    iconCls: 'bg-amber-100 text-amber-600',
    activeCls: 'border-amber-500 bg-amber-50/50 ring-1 ring-amber-500'
  },
  Pinned: {
    icon: Pin,
    iconCls: 'bg-emerald-100 text-emerald-600',
    activeCls: 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
  },
  Email: {
    icon: Mail,
    iconCls: 'bg-purple-100 text-purple-600',
    activeCls: 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500'
  },
  Sticky: {
    icon: Target,
    iconCls: 'bg-rose-100 text-rose-600',
    activeCls: 'border-rose-500 bg-rose-50/50 ring-1 ring-rose-500'
  }
};
export function PromotionTab({
  showToast,
  setActiveAutoService,
  setActiveTab
}: PromotionTabProps) {
  // Dynamic ad slots from shared adData (synced with admin)
  const availableAdSlots = useMemo(() => {
    return ALL_AD_SLOTS.filter(s => s.status === 'active').map(slot => {
      const style = SLOT_ICON_MAP[slot.type] || SLOT_ICON_MAP.Banner;
      const weeklyPrice = Math.round(slot.basePrice / 4);
      const estimatedReach = Math.round(slot.basePrice / 1.5);
      return {
        id: slot.id,
        name: slot.name,
        where: `${slot.page} · ${slot.type} · ${slot.size}`,
        price: `${weeklyPrice.toLocaleString('ru-RU')} ₽ / неделя`,
        priceNum: weeklyPrice,
        reach: `~${estimatedReach.toLocaleString('ru-RU')} показов`,
        requirements: slot.type === 'Pinned' ? 'Профиль заполнен на 80%+' : slot.type === 'Email' ? 'Текст до 200 символов, ссылка на профиль' : `Изображение ${slot.size}, до 50 символов текста`,
        icon: style.icon,
        activeCls: style.activeCls,
        iconCls: style.iconCls,
        slotType: slot.type
      };
    });
  }, []);
  // State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<'pro' | 'top' | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinCategory, setSelectedPinCategory] = useState<string>('');
  const [selectedPinDuration, setSelectedPinDuration] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [showNewAdModal, setShowNewAdModal] = useState(false);
  const [selectedAdSlot, setSelectedAdSlot] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [adOrdersPage, setAdOrdersPage] = useState(1);
  const AD_ORDERS_PER_PAGE = 5;
  // Ad Wizard State
  const [adWizardStep, setAdWizardStep] = useState<1 | 2 | 3>(1);
  const [adMaterials, setAdMaterials] = useState({
    title: '',
    description: '',
    link: '',
    imageUrl: ''
  });
  const [adBalance, setAdBalance] = useState(4500);
  // Mock Data
  const [adOrders, setAdOrders] = useState<AdOrder[]>([{
    id: 'ORD-A-001',
    type: 'banner',
    name: 'Баннер на главной',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '07.03.2026',
    price: 5000,
    views: 12400,
    clicks: 620,
    orders: 18,
    income: 54000,
    adTitle: 'Профессиональный разбор натальной карты',
    adDescription: 'Узнайте свои сильные стороны, кармические задачи и жизненное предназначение. 15 лет опыта, 3000+ консультаций.',
    adLink: '/astrologer/elena-star',
    adImageUrl: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f0?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-002',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'completed',
    startDate: '15.02.2026',
    endDate: '15.02.2026',
    price: 3000,
    views: 10200,
    clicks: 408,
    orders: 12,
    income: 36000,
    adTitle: 'Весенний прогноз 2026 — персональный транзитный отчёт',
    adDescription: 'Специальная акция! Закажите прогноз на весну со скидкой 30%. Узнайте, какие возможности откроются именно для вас.',
    adLink: '/astrologer/elena-star/forecast'
  }, {
    id: 'ORD-A-003',
    type: 'pin',
    name: 'Закреп в каталоге',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '14.03.2026',
    price: 4000,
    views: 8900,
    clicks: 356,
    orders: 9,
    income: 27000,
    adTitle: 'Елена Звёздная — ТОП астролог',
    adDescription: 'Натальные карты, совместимость, прогнозы. Рейтинг 4.9 ★ Более 3000 консультаций.',
    adLink: '/astrologer/elena-star'
  }, {
    id: 'ORD-A-004',
    type: 'banner',
    name: 'Баннер в каталоге',
    status: 'pending_moderation',
    startDate: '10.03.2026',
    endDate: '17.03.2026',
    price: 3500,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    adTitle: 'Кармический анализ — глубокий разбор',
    adDescription: 'Откройте тайны прошлых жизней и поймите свою миссию в этом воплощении. Индивидуальный подход.',
    adLink: '/astrologer/elena-star/karma',
    adImageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-005',
    type: 'pin',
    name: 'Закреп «Натальная карта»',
    status: 'pending_payment',
    startDate: '12.03.2026',
    endDate: '19.03.2026',
    price: 2000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '12.03.2026',
    adTitle: 'Натальная карта Premium — полный разбор личности',
    adDescription: 'PDF-отчёт на 40+ страниц с детальным анализом всех аспектов вашей карты.',
    adLink: '/astrologer/elena-star/natal'
  }, {
    id: 'ORD-A-006',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'rejected',
    startDate: '08.03.2026',
    endDate: null,
    price: 3000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '08.03.2026',
    rejectionReason: 'Текст рекламы содержит запрещённые обещания. Пожалуйста, уберите гарантии результата и отправьте повторно.',
    adTitle: 'Гарантированное изменение судьбы за 1 консультацию!',
    adDescription: 'Я изменю вашу жизнь за одну сессию. 100% гарантия результата. Запишитесь прямо сейчас!',
    adLink: '/astrologer/elena-star'
  }]);
  const activePlacements = [{
    type: 'Закреп в каталоге',
    category: 'Натальная карта',
    daysLeft: 5,
    views: 1247
  }, {
    type: 'Баннер на главной',
    category: 'Все услуги',
    daysLeft: 3,
    views: 4200
  }];
  // Computed
  const totalAdIncome = adOrders.reduce((sum, o) => sum + o.income, 0);
  const totalAdOrders = adOrders.reduce((sum, o) => sum + o.orders, 0);
  const adOrdersTotalPages = Math.ceil(adOrders.length / AD_ORDERS_PER_PAGE);
  const paginatedAdOrders = adOrders.slice((adOrdersPage - 1) * AD_ORDERS_PER_PAGE, adOrdersPage * AD_ORDERS_PER_PAGE);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banner':
        return 'Баннер';
      case 'email':
        return 'Рассылка';
      case 'pin':
        return 'Закреп';
      default:
        return type;
    }
  };
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активен',
          variant: 'success' as const
        };
      case 'completed':
        return {
          label: 'Завершён',
          variant: 'neutral' as const
        };
      case 'pending':
      case 'pending_moderation':
        return {
          label: 'На модерации',
          variant: 'warning' as const
        };
      case 'pending_payment':
        return {
          label: 'Ожидает оплаты',
          variant: 'info' as const
        };
      case 'approved':
        return {
          label: 'Одобрен',
          variant: 'success' as const
        };
      case 'rejected':
        return {
          label: 'Отклонён',
          variant: 'error' as const
        };
      default:
        return {
          label: status,
          variant: 'default' as const
        };
    }
  };
  const handleRepeatOrder = (order: AdOrder) => {
    showToast(`Повторный заказ "${order.name}" создан`);
    console.log(`[Admin Sync] Ad order repeated: ${order.id}`);
  };
  const handleExtendOrder = (order: AdOrder) => {
    showToast(`Продление "${order.name}" оформлено`);
    console.log(`[Admin Sync] Ad order extended: ${order.id}`);
  };
  const handleUpgradeTariff = (tariff: 'pro' | 'top') => {
    setShowTariffModal(false);
    showToast(`Тариф ${tariff === 'pro' ? 'PRO' : 'TOP'} успешно активирован!`);
    console.log(`[Admin Sync] Tariff upgraded to ${tariff}`);
  };
  const handleBuyPin = () => {
    setShowPinModal(false);
    const prices = {
      '1week': 2000,
      '2weeks': 3500,
      '1month': 6000
    };
    showToast(`Закреп "${selectedPinCategory}" оплачен (${prices[selectedPinDuration].toLocaleString('ru-RU')} ₽)`);
    console.log(`[Admin Sync] Pin purchased: ${selectedPinCategory}, duration: ${selectedPinDuration}`);
  };
  return <div className="space-y-6" data-id="element-14536">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-14537">
        <div data-id="element-14538">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14539">
            Продвижение профиля
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-14540">
            Увеличивайте заказы и доход
          </p>
        </div>
      </div>

      {/* Ad Budget */}
      <Card padding="md" className="border-slate-200 bg-white" data-id="element-14541">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14542">
          <div className="flex-1" data-id="element-14543">
            <div className="flex items-center gap-2 mb-2" data-id="element-14544">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center" data-id="element-14545">
                <Wallet className="w-5 h-5 text-purple-600" data-id="element-14546" />
              </div>
              <div data-id="element-14547">
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-14548">
                  Рекламный баланс
                </h3>
                <p className="text-[10px] text-slate-500" data-id="element-14549">
                  Средства для оплаты рекламных размещений
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mt-3" data-id="element-14550">
              <span className="text-3xl font-bold text-slate-900" data-id="element-14551">
                {adBalance.toLocaleString('ru-RU')} ₽
              </span>
              <Badge variant="success" size="sm" data-id="element-14552">
                доступно
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500" data-id="element-14553">
              <span data-id="element-14554">
                Потрачено за месяц:{' '}
                <strong className="text-slate-700" data-id="element-14555">3 500 ₽</strong>
              </span>
              <span data-id="element-14556">
                Остаток:{' '}
                <strong className="text-slate-700" data-id="element-14557">
                  {adBalance.toLocaleString('ru-RU')} ₽
                </strong>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto" data-id="element-14558">
            <Button variant="primary" size="md" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 shadow-sm" icon={<Wallet className="w-4 h-4" data-id="element-14560" />} onClick={() => {
            setAdBalance(prev => prev + 5000);
            showToast('Рекламный баланс пополнен на 5 000 ₽ ✓');
          }} data-id="element-14559">
              Пополнить рекламный баланс
            </Button>
            <p className="text-[10px] text-slate-400 text-center sm:text-right" data-id="element-14561">
              Минимум 1 000 ₽
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-500" data-id="element-14562">
          <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" data-id="element-14563" />
          <span data-id="element-14564">
            Реклама списывается с рекламного баланса. Если средств не хватает —
            пополните баланс перед оформлением заказа.
          </span>
        </div>
      </Card>

      {/* Section 1: Current Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-14565">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => showToast('Текущий тариф: Базовый')} data-id="element-14566">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14567">Тариф</div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-14568">
            Базовый <Crown className="w-4 h-4 text-amber-400" data-id="element-14569" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Позиция #47 в каталоге');
      }} data-id="element-14570">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14571">Позиция</div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14572">#47</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('1 247 просмотров за неделю');
      }} data-id="element-14573">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14574">
            Просмотры
          </div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14575">1 247</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Конверсия 8.3%');
      }} data-id="element-14576">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14577">
            Конверсия
          </div>
          <div className="text-lg font-bold text-emerald-600" data-id="element-14578">8.3%</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-emerald-50/50 border-emerald-200" onClick={() => showToast('Доход с продвижения за месяц')} data-id="element-14579">
          <div className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-14580">
            Доход с рекламы
          </div>
          <div className="text-lg font-bold text-emerald-700" data-id="element-14581">
            {totalAdIncome.toLocaleString('ru-RU')} ₽
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-indigo-50/50 border-indigo-200" onClick={() => showToast('Заказы с продвижения за месяц')} data-id="element-14582">
          <div className="text-xs text-indigo-600 mb-1 font-medium" data-id="element-14583">
            Заказы с рекламы
          </div>
          <div className="text-lg font-bold text-indigo-700" data-id="element-14584">
            {totalAdOrders}
          </div>
        </Card>
      </div>

      {/* Section 2: CTA Block — How to increase income */}
      <Card padding="md" className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white border-none relative overflow-hidden" data-id="element-14585">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl" data-id="element-14586"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-14587">
          <div className="flex-1" data-id="element-14588">
            <div className="flex items-center gap-2 mb-2" data-id="element-14589">
              <Sparkles className="w-5 h-5 text-amber-300" data-id="element-14590" />
              <h3 className="text-xl font-bold" data-id="element-14591">Как увеличить доход</h3>
            </div>
            <p className="text-indigo-100 text-sm max-w-lg" data-id="element-14592">
              Перейдите на тариф <strong className="text-white" data-id="element-14593">PRO</strong> и
              получите приоритет в каталоге. По статистике, PRO-астрологи
              получают{' '}
              <strong className="text-white" data-id="element-14594">на 40% больше заказов</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-14595">
            <button className="px-5 py-2.5 bg-white text-indigo-700 font-bold text-sm rounded-lg shadow-lg hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" onClick={() => {
            setSelectedTariff('pro');
            setShowTariffModal(true);
          }} data-id="element-14596">
              <Crown className="w-4 h-4" data-id="element-14597" />
              Перейти на PRO
            </button>
            <button className="px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white font-bold text-sm rounded-lg border-2 border-white/50 hover:bg-white/30 transition-colors flex items-center justify-center gap-2" onClick={() => setShowNewAdModal(true)} data-id="element-14598">
              <Megaphone className="w-4 h-4" data-id="element-14599" />
              Включить рекламу
            </button>
          </div>
        </div>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-14600">
        {[{
        icon: Rocket,
        label: 'Запустить рекламу',
        color: 'text-indigo-600 bg-indigo-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: ArrowUpCircle,
        label: 'Поднять профиль',
        color: 'text-amber-600 bg-amber-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: Mail,
        label: 'Включить рассылку',
        color: 'text-emerald-600 bg-emerald-50',
        action: () => showToast('Рассылка включена — клиенты получат уведомление о ваших услугах')
      }, {
        icon: Crown,
        label: 'Улучшить тариф',
        color: 'text-purple-600 bg-purple-50',
        action: () => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }
      }].map((item, i) => <button key={i} onClick={item.action} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all text-center group" data-id="element-14601">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`} data-id="element-14602">
              <item.icon className="w-5 h-5" data-id="element-14603" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-14604">
              {item.label}
            </span>
          </button>)}
      </div>

      {/* Section 4: Ad Orders */}
      <Card padding="none" className="overflow-hidden" data-id="element-14605">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14606">
          <div data-id="element-14607">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-14608">
              Мои рекламные заказы
            </h3>
            <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-indigo-600 font-medium hover:underline mt-0.5" data-id="element-14609">
              {showHowItWorks ? 'Скрыть справку' : 'Как работает реклама?'}
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<Megaphone className="w-4 h-4" data-id="element-14611" />} onClick={() => setShowNewAdModal(true)} data-id="element-14610">
            Новый заказ
          </Button>
        </div>
        {showHowItWorks && <div className="px-4 sm:px-5 py-4 bg-indigo-50/50 border-b border-indigo-100 animate-in fade-in slide-in-from-top-2" data-id="element-14612">
            <h4 className="font-bold text-indigo-900 text-sm mb-2" data-id="element-14613">
              Как работает реклама на платформе
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-indigo-800" data-id="element-14614">
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14615">
                <div className="font-bold mb-1" data-id="element-14616">1. Оформление и оплата</div>
                <p className="text-indigo-600" data-id="element-14617">
                  Выберите формат, загрузите материалы и оплатите с рекламного
                  баланса.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14618">
                <div className="font-bold mb-1" data-id="element-14619">2. Модерация (1-12 часов)</div>
                <p className="text-indigo-600" data-id="element-14620">
                  Администратор проверит материалы. Результат придет в системные
                  уведомления (колокольчик).
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14621">
                <div className="font-bold mb-1" data-id="element-14622">3. Запуск и статистика</div>
                <p className="text-indigo-600" data-id="element-14623">
                  После одобрения реклама запустится автоматически. Отслеживайте
                  просмотры и клики здесь.
                </p>
              </div>
            </div>
          </div>}

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-14624">
          <table className="w-full text-left text-sm" data-id="element-14625">
            <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500" data-id="element-14626">
              <tr data-id="element-14627">
                <th className="px-5 py-3 font-semibold" data-id="element-14628">Услуга</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14629">Тип</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14630">Статус</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14631">Период</th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14632">
                  Стоимость
                </th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14633">
                  Результат
                </th>
                <th className="px-5 py-3 w-10" data-id="element-14634"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-14635">
              {paginatedAdOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const statusConfig = getStatusConfig(order.status);
              return <Fragment key={order.id} data-id="element-14636">
                    <tr className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/80' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14637">
                      <td className="px-5 py-3" data-id="element-14638">
                        <div className="font-bold text-slate-900" data-id="element-14639">
                          {order.name}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-14640">
                          {order.id}
                        </div>
                      </td>
                      <td className="px-5 py-3" data-id="element-14641">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-14642">
                          {getTypeLabel(order.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-14643">
                        <Badge variant={statusConfig.variant} size="sm" data-id="element-14644">
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs" data-id="element-14645">
                        {order.endDate ? `${order.startDate} — ${order.endDate}` : `С ${order.startDate}`}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-14646">
                        {order.price.toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="px-5 py-3 text-right" data-id="element-14647">
                        <span className="text-xs text-slate-500" data-id="element-14648">
                          {order.clicks} кликов / {order.orders} заказов
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400" data-id="element-14649">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-14650" /> : <ChevronDown className="w-5 h-5" data-id="element-14651" />}
                      </td>
                    </tr>

                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-14652">
                        <td colSpan={7} className="px-5 py-4" data-id="element-14653">
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2" data-id="element-14654">
                            {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-4" data-id="element-14655">
                                <div className="flex items-center gap-2 mb-2" data-id="element-14656">
                                  {order.status === 'rejected' ? <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-14657" /> : order.status === 'pending_payment' ? <Wallet className="w-5 h-5 text-indigo-500" data-id="element-14658" /> : <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-14659" />}
                                  <h4 className="font-bold text-slate-900 text-sm" data-id="element-14660">
                                    {order.status === 'rejected' ? 'Заказ отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Заказ на модерации'}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-600" data-id="element-14661">
                                  {order.status === 'rejected' ? 'Модерация не пройдена. Исправьте замечания и отправьте повторно.' : order.status === 'pending_payment' ? 'Модерация пройдена! Оплатите заказ для запуска рекламы.' : 'Ваша реклама проверяется администратором. Обычно это занимает 1–12 часов.'}
                                </p>

                                {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-id="element-14662">
                                      <div className="font-bold mb-1" data-id="element-14663">
                                        Причина отклонения:
                                      </div>
                                      {order.rejectionReason}
                                    </div>}

                                <div className="relative pt-2 pb-2" data-id="element-14664">
                                  <div className="absolute left-3 top-6 bottom-4 w-0.5 bg-slate-200" data-id="element-14665"></div>

                                  {/* Шаг 1: Оформлен */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14666">
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14667">
                                      <Check className="w-3 h-3" data-id="element-14668" />
                                    </div>
                                    <div data-id="element-14669">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14670">
                                        Оформлен
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14671">
                                        {order.startDate}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 2: Модерация */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14672">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14673">
                                      {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14674" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14675" /> : <Clock className="w-3 h-3" data-id="element-14676" />}
                                    </div>
                                    <div data-id="element-14677">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14678">
                                        Модерация
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14679">
                                        {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 3: Оплата */}
                                  <div className={`flex gap-3 mb-3 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14680">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-14681">
                                      <Wallet className="w-3 h-3" data-id="element-14682" />
                                    </div>
                                    <div data-id="element-14683">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14684">
                                        Оплата
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : order.status === 'approved' ? 'text-emerald-600' : 'text-slate-500'}`} data-id="element-14685">
                                        {order.status === 'pending_payment' ? 'Ожидает оплаты' : order.status === 'approved' ? 'Оплачено' : 'Ожидается'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 4: Запуск */}
                                  <div className={`flex gap-3 relative z-10 ${order.status !== 'approved' ? 'opacity-40' : ''}`} data-id="element-14686">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14687">
                                      <Rocket className="w-3 h-3" data-id="element-14688" />
                                    </div>
                                    <div data-id="element-14689">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14690">
                                        Запуск
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14691">
                                        Ожидается
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-14692">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14693">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14694" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14695">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14696">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14697">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14698" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14699">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14700">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14701">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14702">
                                            <Globe className="w-3 h-3" data-id="element-14703" />
                                            <span className="truncate" data-id="element-14704">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2 pt-2" data-id="element-14705">
                                  {order.status === 'pending_payment' && <Button variant="primary" size="sm" icon={<Wallet className="w-3.5 h-3.5" data-id="element-14707" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'active' as const
                            } : o));
                            setAdBalance(prev => prev - order.price);
                            showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                          }} data-id="element-14706">
                                      Оплатить{' '}
                                      {order.price.toLocaleString('ru-RU')} ₽
                                    </Button>}
                                  {order.status === 'rejected' && <Button variant="primary" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14709" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'pending_moderation' as const,
                              rejectionReason: undefined
                            } : o));
                            showToast('Заявка отправлена повторно на модерацию');
                          }} data-id="element-14708">
                                      Исправить и отправить
                                    </Button>}
                                </div>
                              </div> : <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-5" data-id="element-14710">
                                  <div data-id="element-14711">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14712">
                                      <Eye className="w-3 h-3" data-id="element-14713" /> Просмотры
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14714">
                                      {order.views.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14715">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14716">
                                      <MousePointerClick className="w-3 h-3" data-id="element-14717" />{' '}
                                      Клики
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14718">
                                      {order.clicks.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14719">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14720">
                                      <ShoppingBag className="w-3 h-3" data-id="element-14721" /> Заказы
                                    </div>
                                    <div className="text-xl font-bold text-indigo-600" data-id="element-14722">
                                      {order.orders}
                                    </div>
                                  </div>
                                  <div data-id="element-14723">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14724">
                                      <DollarSign className="w-3 h-3" data-id="element-14725" /> Доход
                                    </div>
                                    <div className="text-xl font-bold text-emerald-600" data-id="element-14726">
                                      {order.income.toLocaleString('ru-RU')} ₽
                                    </div>
                                  </div>
                                </div>

                                <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-14727">
                                  CTR:{' '}
                                  <strong className="text-slate-700" data-id="element-14728">
                                    {(order.clicks / order.views * 100).toFixed(1)}
                                    %
                                  </strong>{' '}
                                  · Конверсия:{' '}
                                  <strong className="text-slate-700" data-id="element-14729">
                                    {order.clicks > 0 ? (order.orders / order.clicks * 100).toFixed(1) : 0}
                                    %
                                  </strong>{' '}
                                  · ROI:{' '}
                                  <strong className={order.income > order.price ? 'text-emerald-600' : 'text-rose-600'} data-id="element-14730">
                                    {order.price > 0 ? ((order.income - order.price) / order.price * 100).toFixed(0) : 0}
                                    %
                                  </strong>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mb-4 pt-2" data-id="element-14731">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14732">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14733" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14734">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14735">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14736">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14737" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14738">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14739">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14740">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14741">
                                            <Globe className="w-3 h-3" data-id="element-14742" />
                                            <span className="truncate" data-id="element-14743">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                <div className="flex gap-2" data-id="element-14744">
                                  <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14746" />} onClick={e => {
                            e.stopPropagation();
                            handleRepeatOrder(order);
                          }} data-id="element-14745">
                                    Повторить
                                  </Button>
                                  {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14748" />} onClick={e => {
                            e.stopPropagation();
                            handleExtendOrder(order);
                          }} data-id="element-14747">
                                      Продлить
                                    </Button>}
                                </div>
                              </>}
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-14749">
          {paginatedAdOrders.map(order => {
          const isExpanded = expandedOrderId === order.id;
          const statusConfig = getStatusConfig(order.status);
          return <div key={order.id} data-id="element-14750">
                <div className={`p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14751">
                  <div className="flex justify-between items-start mb-2" data-id="element-14752">
                    <div data-id="element-14753">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-14754">
                        {order.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-14755">
                        {getTypeLabel(order.type)} · {order.id}
                      </div>
                    </div>
                    <Badge variant={statusConfig.variant} size="sm" data-id="element-14756">
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-14757">
                    <span className="text-xs text-slate-500" data-id="element-14758">
                      {order.startDate}
                      {order.endDate ? ` — ${order.endDate}` : ''}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-14759">
                      {order.price.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>

                {isExpanded && <div className="p-4 bg-slate-50 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-14760">
                    {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-3" data-id="element-14761">
                        <div className="flex items-center gap-2" data-id="element-14762">
                          {order.status === 'rejected' ? <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-14763" /> : order.status === 'pending_payment' ? <Wallet className="w-4 h-4 text-indigo-500" data-id="element-14764" /> : <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-14765" />}
                          <h4 className="font-bold text-slate-900 text-sm" data-id="element-14766">
                            {order.status === 'rejected' ? 'Отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'На модерации'}
                          </h4>
                        </div>

                        {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-700" data-id="element-14767">
                              <div className="font-bold mb-0.5" data-id="element-14768">Причина:</div>
                              {order.rejectionReason}
                            </div>}

                        <div className="relative pt-1 pb-1" data-id="element-14769">
                          <div className="absolute left-3 top-5 bottom-3 w-0.5 bg-slate-200" data-id="element-14770"></div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14771">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14772">
                              <Check className="w-3 h-3" data-id="element-14773" />
                            </div>
                            <div data-id="element-14774">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14775">
                                Оформлен
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14776">
                                {order.startDate}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14777">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14778">
                              {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14779" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14780" /> : <Clock className="w-3 h-3" data-id="element-14781" />}
                            </div>
                            <div data-id="element-14782">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14783">
                                Модерация
                              </div>
                              <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14784">
                                {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                              </div>
                            </div>
                          </div>

                          <div className={`flex gap-3 mb-2.5 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14785">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : 'bg-slate-100 text-slate-400'}`} data-id="element-14786">
                              <Wallet className="w-3 h-3" data-id="element-14787" />
                            </div>
                            <div data-id="element-14788">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14789">
                                Оплата
                              </div>
                              <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : 'text-slate-500'}`} data-id="element-14790">
                                {order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Ожидается'}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 relative z-10 opacity-40" data-id="element-14791">
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14792">
                              <Rocket className="w-3 h-3" data-id="element-14793" />
                            </div>
                            <div data-id="element-14794">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14795">
                                Запуск
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14796">
                                Ожидается
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 border-t border-slate-200" data-id="element-14797">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14798">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14799" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14800">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14801">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14802">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14803" />
                                </div>}
                              <div className="p-2.5" data-id="element-14804">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14805">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14806">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14807">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14808" />
                                    <span className="truncate" data-id="element-14809">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex flex-wrap gap-2" data-id="element-14810">
                          {order.status === 'pending_payment' && <Button variant="primary" size="sm" fullWidth icon={<Wallet className="w-3.5 h-3.5" data-id="element-14812" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'active' as const
                    } : o));
                    setAdBalance(prev => prev - order.price);
                    showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                  }} data-id="element-14811">
                              Оплатить {order.price.toLocaleString('ru-RU')} ₽
                            </Button>}
                          {order.status === 'rejected' && <Button variant="primary" size="sm" fullWidth icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14814" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'pending_moderation' as const,
                      rejectionReason: undefined
                    } : o));
                    showToast('Заявка отправлена повторно на модерацию');
                  }} data-id="element-14813">
                              Исправить и отправить
                            </Button>}
                        </div>
                      </div> : <>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-14815">
                          <div data-id="element-14816">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14817">
                              Просмотры
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14818">
                              {order.views.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14819">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14820">
                              Клики
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14821">
                              {order.clicks.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14822">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14823">
                              Заказы
                            </div>
                            <div className="font-bold text-indigo-600" data-id="element-14824">
                              {order.orders}
                            </div>
                          </div>
                          <div data-id="element-14825">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14826">
                              Доход
                            </div>
                            <div className="font-bold text-emerald-600" data-id="element-14827">
                              {order.income.toLocaleString('ru-RU')} ₽
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия (активные/завершённые) */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 mb-3 border-t border-slate-200" data-id="element-14828">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14829">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14830" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14831">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14832">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14833">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14834" />
                                </div>}
                              <div className="p-2.5" data-id="element-14835">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14836">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14837">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14838">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14839" />
                                    <span className="truncate" data-id="element-14840">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex gap-2" data-id="element-14841">
                          <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14843" />} onClick={e => {
                    e.stopPropagation();
                    handleRepeatOrder(order);
                  }} data-id="element-14842">
                            Повторить
                          </Button>
                          {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14845" />} onClick={e => {
                    e.stopPropagation();
                    handleExtendOrder(order);
                  }} data-id="element-14844">
                              Продлить
                            </Button>}
                        </div>
                      </>}
                  </div>}
              </div>;
        })}
        </div>

        {/* Ad Orders Pagination */}
        {adOrdersTotalPages > 1 && <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-14846">
            <span className="text-xs text-slate-500" data-id="element-14847">
              Показано {(adOrdersPage - 1) * AD_ORDERS_PER_PAGE + 1}–
              {Math.min(adOrdersPage * AD_ORDERS_PER_PAGE, adOrders.length)} из{' '}
              {adOrders.length}
            </span>
            <Pagination currentPage={adOrdersPage} totalPages={adOrdersTotalPages} onPageChange={setAdOrdersPage} data-id="element-14848" />
          </div>}
      </Card>

      {/* Section 6: Active Placements */}
      <Card padding="md" data-id="element-14849">
        <div className="flex justify-between items-center mb-4" data-id="element-14850">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14851">
            <Target className="w-5 h-5 text-amber-500" data-id="element-14852" /> Где вы размещены
          </h3>
          <Button variant="outline" size="sm" onClick={() => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }} data-id="element-14853">
            Купить размещение
          </Button>
        </div>

        {activePlacements.length > 0 ? <div className="space-y-3" data-id="element-14854">
            {activePlacements.map((p, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-amber-100 bg-amber-50/30" data-id="element-14855">
                <div className="flex items-center gap-3" data-id="element-14856">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-14857">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-14858" />
                  </div>
                  <div data-id="element-14859">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-14860">
                      {p.type}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-14861">{p.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm" data-id="element-14862">
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14863">
                    <Clock className="w-3.5 h-3.5 text-amber-500" data-id="element-14864" />
                    <span data-id="element-14865">{p.daysLeft} дн.</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14866">
                    <Eye className="w-3.5 h-3.5 text-indigo-500" data-id="element-14867" />
                    <span data-id="element-14868">{p.views.toLocaleString('ru-RU')}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 h-8 px-2" onClick={() => {
              setShowPinModal(true);
              setSelectedPinCategory(p.category);
            }} data-id="element-14869">
                    Продлить
                  </Button>
                </div>
              </div>)}
          </div> : <div className="text-center py-8 text-slate-500" data-id="element-14870">
            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-14871" />
            <p className="text-sm" data-id="element-14872">У вас нет активных размещений</p>
          </div>}
      </Card>

      {/* Section 7: Simple Analytics */}
      <Card padding="md" data-id="element-14873">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14874">
          <BarChart3 className="w-5 h-5 text-indigo-500" data-id="element-14875" /> Эффективность за
          месяц
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-14876">
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14877">
            <Eye className="w-5 h-5 text-slate-400 mx-auto mb-2" data-id="element-14878" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14879">1 247</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14880">Просмотры</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14881">
            <MousePointerClick className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-14882" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14883">620</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14884">Клики</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-14885">
            <ShoppingBag className="w-5 h-5 text-indigo-500 mx-auto mb-2" data-id="element-14886" />
            <div className="text-2xl font-bold text-indigo-700" data-id="element-14887">39</div>
            <div className="text-xs text-indigo-600 mt-1" data-id="element-14888">Заказы</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-14889">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-2" data-id="element-14890" />
            <div className="text-2xl font-bold text-emerald-700" data-id="element-14891">117 000 ₽</div>
            <div className="text-xs text-emerald-600 mt-1" data-id="element-14892">Доход</div>
          </div>
        </div>
      </Card>

      {/* Tariff Upgrade Modal */}
      {showTariffModal && selectedTariff && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14893">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14894">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14895">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14896">
                Переход на {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
              </h3>
              <button onClick={() => setShowTariffModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14897">
                <X className="w-5 h-5" data-id="element-14898" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14899">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14900">
                <div className="flex justify-between text-sm mb-3" data-id="element-14901">
                  <span className="text-slate-500" data-id="element-14902">Тариф</span>
                  <span className="font-bold text-slate-900" data-id="element-14903">
                    {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14904">
                  <span className="text-slate-500" data-id="element-14905">Стоимость</span>
                  <span className="font-bold text-slate-900" data-id="element-14906">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽ / мес
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14907">
                  <span className="text-slate-500" data-id="element-14908">Ожидаемый рост заказов</span>
                  <span className="font-bold text-emerald-600" data-id="element-14909">
                    {selectedTariff === 'pro' ? '+40%' : '+120%'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200" data-id="element-14910">
                  <span className="font-bold text-slate-900" data-id="element-14911">
                    К оплате сейчас
                  </span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14912">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-14913">
                Оплата будет списана с вашего баланса. Тариф активируется
                мгновенно. Отмена возможна в любое время.
              </p>

              <div className="flex gap-3" data-id="element-14914">
                <Button variant="outline" fullWidth onClick={() => setShowTariffModal(false)} data-id="element-14915">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={() => handleUpgradeTariff(selectedTariff)} data-id="element-14916">
                  Оплатить и активировать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* New Ad Order Modal */}
      {showNewAdModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14917">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto" data-id="element-14918">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-14919">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14920">
                Создать рекламу
              </h3>
              <button onClick={() => {
            setShowNewAdModal(false);
            setSelectedAdSlot(null);
            setAdWizardStep(1);
            setAdMaterials({
              title: '',
              description: '',
              link: '',
              imageUrl: ''
            });
          }} className="text-slate-400 hover:text-slate-600" data-id="element-14921">
                <X className="w-5 h-5" data-id="element-14922" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-14923">
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6 relative" data-id="element-14924">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-14925"></div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-indigo-500 -z-10 transition-all duration-300" style={{
              width: `${(adWizardStep - 1) * 50}%`
            }} data-id="element-14926"></div>

                {[1, 2, 3].map(step => <div key={step} className="flex flex-col items-center gap-1 bg-white px-2" data-id="element-14927">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${adWizardStep >= step ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`} data-id="element-14928">
                      {adWizardStep > step ? <Check className="w-4 h-4" data-id="element-14929" /> : step}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${adWizardStep >= step ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-14930">
                      {step === 1 ? 'Формат' : step === 2 ? 'Материалы' : 'Оплата'}
                    </span>
                  </div>)}
              </div>

              {adWizardStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14931">
                  <p className="text-sm text-slate-600" data-id="element-14932">
                    Выберите рекламный слот. Каждый формат показывается в разных
                    местах сайта.
                  </p>

                  <div className="max-h-[45vh] overflow-y-auto space-y-3 pr-1 -mr-1" data-id="element-14933">
                    {availableAdSlots.map(slot => <button key={slot.id} onClick={() => setSelectedAdSlot(selectedAdSlot === slot.id ? null : slot.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAdSlot === slot.id ? slot.activeCls : 'border-slate-200 hover:border-slate-300'}`} data-id="element-14934">
                        <div className="flex items-start gap-3" data-id="element-14935">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${slot.iconCls}`} data-id="element-14936">
                            <slot.icon className="w-5 h-5" data-id="element-14937" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-14938">
                            <div className="flex items-center justify-between gap-2" data-id="element-14939">
                              <span className="font-bold text-slate-900 text-sm" data-id="element-14940">
                                {slot.name}
                              </span>
                              <span className="text-xs font-bold text-indigo-600 whitespace-nowrap" data-id="element-14941">
                                {slot.price}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-14942">
                              {slot.where}
                            </p>
                            {selectedAdSlot === slot.id && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 animate-in fade-in" data-id="element-14943">
                                <div className="flex items-center gap-2 text-xs" data-id="element-14944">
                                  <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-14945" />
                                  <span className="text-slate-600" data-id="element-14946">
                                    Охват:{' '}
                                    <strong className="text-slate-900" data-id="element-14947">
                                      {slot.reach}
                                    </strong>
                                  </span>
                                </div>
                                <div className="flex items-start gap-2 text-xs" data-id="element-14948">
                                  <AlertCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-14949" />
                                  <span className="text-slate-600" data-id="element-14950">
                                    Требования:{' '}
                                    <strong className="text-slate-900" data-id="element-14951">
                                      {slot.requirements}
                                    </strong>
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>
                      </button>)}
                  </div>

                  <div className="flex justify-end pt-2" data-id="element-14952">
                    <Button variant="primary" disabled={!selectedAdSlot} onClick={() => {
                const selected = availableAdSlots.find(s => s.id === selectedAdSlot);
                if (selected?.slotType === 'Pinned') {
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setShowPinModal(true);
                  setSelectedPinCategory('Натальная карта');
                } else {
                  setAdWizardStep(2);
                }
              }} icon={<ArrowRight className="w-4 h-4" data-id="element-14954" />} data-id="element-14953">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 2 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14955">
                  <div data-id="element-14956">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14957">
                      Заголовок рекламы
                    </label>
                    <input type="text" value={adMaterials.title} onChange={e => setAdMaterials({
                ...adMaterials,
                title: e.target.value
              })} placeholder="Например: Профессиональный разбор натальной карты" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14958" />
                    <div className="text-right text-xs text-slate-400 mt-1" data-id="element-14959">
                      {adMaterials.title.length}/50
                    </div>
                  </div>

                  <div data-id="element-14960">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14961">
                      Описание
                    </label>
                    <textarea value={adMaterials.description} onChange={e => setAdMaterials({
                ...adMaterials,
                description: e.target.value
              })} placeholder="Кратко опишите ваше предложение..." rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-id="element-14962" />
                  </div>

                  <div data-id="element-14963">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14964">
                      Ссылка (куда вести)
                    </label>
                    <input type="text" value={adMaterials.link} onChange={e => setAdMaterials({
                ...adMaterials,
                link: e.target.value
              })} placeholder="https://astroguide.ru/profile/elena" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14965" />
                  </div>

                  <div data-id="element-14966">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14967">
                      Баннер / Изображение
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 hover:border-indigo-300 cursor-pointer transition-colors relative overflow-hidden group" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const url = URL.createObjectURL(file);
                    setAdMaterials({
                      ...adMaterials,
                      imageUrl: url
                    });
                    showToast(`Изображение загружено ✓`);
                  }
                };
                input.click();
              }} data-id="element-14968">
                      {adMaterials.imageUrl ? <>
                          <img src={adMaterials.imageUrl} alt="Banner" className="absolute inset-0 w-full h-full object-cover" data-id="element-14969" />
                          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-14970">
                            <span className="text-white font-medium text-sm" data-id="element-14971">
                              Заменить изображение
                            </span>
                          </div>
                        </> : <>
                          <LayoutGrid className="w-8 h-8 text-slate-400 mx-auto mb-2" data-id="element-14972" />
                          <div className="text-sm font-bold text-slate-700 mb-1" data-id="element-14973">
                            Загрузить изображение
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-14974">
                            JPG, PNG до 5 МБ
                          </div>
                        </>}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-14975">
                    <Button variant="outline" onClick={() => setAdWizardStep(1)} data-id="element-14976">
                      Назад
                    </Button>
                    <Button variant="primary" disabled={!adMaterials.title || !adMaterials.imageUrl} onClick={() => setAdWizardStep(3)} icon={<ArrowRight className="w-4 h-4" data-id="element-14978" />} data-id="element-14977">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 3 && (() => {
            const selectedSlot = availableAdSlots.find(s => s.id === selectedAdSlot);
            const price = selectedSlot?.priceNum || 0;
            const slotName = selectedSlot?.name || 'Реклама';
            const slotType = selectedSlot?.slotType === 'Email' ? 'email' : selectedSlot?.slotType === 'Pinned' ? 'pin' : 'banner';
            return <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14979">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14980">
                        <h4 className="font-bold text-slate-900 mb-3" data-id="element-14981">
                          Сводка заказа
                        </h4>

                        <div className="space-y-2 mb-4" data-id="element-14982">
                          <div className="flex justify-between text-sm" data-id="element-14983">
                            <span className="text-slate-500" data-id="element-14984">Формат</span>
                            <span className="font-medium text-slate-900" data-id="element-14985">
                              {slotName}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-14986">
                            <span className="text-slate-500" data-id="element-14987">Период</span>
                            <span className="font-medium text-slate-900" data-id="element-14988">
                              1 неделя
                            </span>
                          </div>
                        </div>

                        <div className="p-3 bg-white rounded-lg border border-slate-200 mb-4" data-id="element-14989">
                          <div className="text-xs text-slate-500 mb-2" data-id="element-14990">
                            Превью:
                          </div>
                          <div className="flex gap-3 items-center" data-id="element-14991">
                            <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-14992">
                              {adMaterials.imageUrl && <img src={adMaterials.imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-14993" />}
                            </div>
                            <div className="min-w-0" data-id="element-14994">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-14995">
                                {adMaterials.title}
                              </div>
                              <div className="text-xs text-slate-500 truncate" data-id="element-14996">
                                {adMaterials.description || 'Без описания'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-14997">
                          <span className="font-bold text-slate-900" data-id="element-14998">
                            К оплате
                          </span>
                          <span className="text-xl font-bold text-indigo-600" data-id="element-14999">
                            {price.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-15000">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-2" data-id="element-15001">
                          <span className="text-sm font-bold text-indigo-900 flex items-center gap-2" data-id="element-15002">
                            <Wallet className="w-4 h-4" data-id="element-15003" /> Рекламный баланс
                          </span>
                          <span className="font-bold text-indigo-700" data-id="element-15004">
                            {adBalance.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>

                        {adBalance < price ? <div className="text-xs text-rose-600 mt-2 flex items-start gap-1" data-id="element-15005">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-15006" />
                            <span data-id="element-15007">
                              Недостаточно средств. Пополните баланс на{' '}
                              {(price - adBalance).toLocaleString('ru-RU')} ₽
                            </span>
                          </div> : <div className="text-xs text-indigo-600 mt-2" data-id="element-15008">
                            Остаток после оплаты:{' '}
                            {(adBalance - price).toLocaleString('ru-RU')} ₽
                          </div>}
                      </div>

                      <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-15009">
                        <Button variant="outline" onClick={() => setAdWizardStep(2)} data-id="element-15010">
                          Назад
                        </Button>
                        <Button variant="primary" disabled={adBalance < price} onClick={() => {
                  const newOrder: AdOrder = {
                    id: `ORD-A-00${adOrders.length + 1}`,
                    type: slotType as any,
                    name: slotName,
                    status: 'pending',
                    startDate: new Date().toLocaleDateString('ru-RU'),
                    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU'),
                    price: price,
                    views: 0,
                    clicks: 0,
                    orders: 0,
                    income: 0
                  };
                  setAdOrders([newOrder, ...adOrders]);
                  setAdBalance(adBalance - price);
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setAdWizardStep(1);
                  setAdMaterials({
                    title: '',
                    description: '',
                    link: '',
                    imageUrl: ''
                  });
                  showToast('Заказ оплачен и отправлен на модерацию. Вы получите уведомление о статусе.');
                }} data-id="element-15011">
                          Оплатить и отправить
                        </Button>
                      </div>
                    </div>;
          })()}
            </div>
          </div>
        </div>}

      {/* Pin Purchase Modal */}
      {showPinModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-15012">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-15013">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-15014">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-15015">
                Купить размещение
              </h3>
              <button onClick={() => setShowPinModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-15016">
                <X className="w-5 h-5" data-id="element-15017" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-15018">
              <div data-id="element-15019">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-15020">
                  Категория
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={selectedPinCategory} onChange={e => setSelectedPinCategory(e.target.value)} data-id="element-15021">
                  <option value="Натальная карта" data-id="element-15022">Натальная карта</option>
                  <option value="Совместимость" data-id="element-15023">Совместимость</option>
                  <option value="Соляр" data-id="element-15024">Соляр на год</option>
                  <option value="Транзиты" data-id="element-15025">Транзиты</option>
                </select>
              </div>

              <div data-id="element-15026">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-15027">
                  Длительность
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-15028">
                  {[{
                value: '1week' as const,
                label: '1 неделя',
                price: '2 000 ₽'
              }, {
                value: '2weeks' as const,
                label: '2 недели',
                price: '3 500 ₽'
              }, {
                value: '1month' as const,
                label: '1 месяц',
                price: '6 000 ₽'
              }].map(opt => <button key={opt.value} onClick={() => setSelectedPinDuration(opt.value)} className={`p-3 rounded-xl border text-center transition-all ${selectedPinDuration === opt.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-15029">
                      <div className="text-sm font-bold text-slate-900" data-id="element-15030">
                        {opt.label}
                      </div>
                      <div className="text-xs text-indigo-600 font-medium mt-1" data-id="element-15031">
                        {opt.price}
                      </div>
                    </button>)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-15032">
                <div className="flex justify-between text-sm mb-2" data-id="element-15033">
                  <span className="text-slate-500" data-id="element-15034">Категория</span>
                  <span className="font-medium text-slate-900" data-id="element-15035">
                    {selectedPinCategory}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-15036">
                  <span className="font-bold text-slate-900" data-id="element-15037">К оплате</span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-15038">
                    {selectedPinDuration === '1week' ? '2 000' : selectedPinDuration === '2weeks' ? '3 500' : '6 000'}{' '}
                    ₽
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-15039">
                <Button variant="outline" fullWidth onClick={() => setShowPinModal(false)} data-id="element-15040">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={handleBuyPin} data-id="element-15041">
                  Оплатить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}